import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import { FormattedMessage } from "react-intl";

import BaseFormCard from "../../base/BaseFormCard";
import ArrowForwardIcon from "../../../assets/icons/ArrowForwardIcon";
import {
  CARD_PAPER_STYLE,
  CARD_PAPER_SX,
  CARD_HEADER_SX,
  CARD_TITLE_SX,
  CARD_BODY_SX,
  CARD_DATE_SX,
  CARD_ARROW_SX
} from "./subscribed-item";

/**
 * Premium accent treatment (BO-869): sets this card apart from a plain
 * subscribed-item card so it reads as a recommended upsell, not just another
 * row in the list. Uses the light/dark-aware semantic tokens from
 * colors_v2.css (not the raw --info-innovation-* palette values, which are
 * pinned to the same hex in both themes and don't adapt in dark mode).
 */
const CROSS_SELL_PAPER_STYLE = {
  ...CARD_PAPER_STYLE,
  border: "1px solid var(--tabs-functional-active-border-information)",
  borderLeft: "3px solid var(--tabs-functional-active-border-information)",
  backgroundColor: "var(--tabs-functional-active-background-information)"
};

/**
 * A product the merchant does not own yet, offered as a cross-sell (BO-869).
 *
 * Same card chrome as SubscribedItem so the section reads as one list, but the
 * click navigates in-app to the product's pricing page (not an external
 * signup), because the merchant is already logged in and already paying.
 */
export default function CrossSellItem({
  productType,
  nameKey,
  descriptionKey,
  onClick
}) {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick?.(event);
    }
  };

  return (
    <BaseFormCard
      noForm
      headerBorder={false}
      title={nameKey}
      titleProps={{
        sx: CARD_TITLE_SX,
        "data-testid": `cross-sell-item-${productType}-name`
      }}
      paperStyle={CROSS_SELL_PAPER_STYLE}
      paperSx={CARD_PAPER_SX}
      headerSx={CARD_HEADER_SX}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      data-testid={`cross-sell-item-${productType}`}
    >
      <Box sx={CARD_BODY_SX}>
        <Typography
          data-testid={`cross-sell-item-${productType}-description`}
          component="p"
          sx={{ ...CARD_DATE_SX, m: 0 }}
        >
          <FormattedMessage id={descriptionKey} defaultMessage={descriptionKey} />
        </Typography>
      </Box>
      <Box
        sx={CARD_ARROW_SX}
        data-testid={`cross-sell-item-${productType}-arrow`}
      >
        <ArrowForwardIcon color="var(--tabs-functional-active-border-information)" />
      </Box>
    </BaseFormCard>
  );
}
